import { AdPlacement } from "@/components/ads/AdPlacement";
import {
  SciBreadcrumb,
  SciExplainer,
  SciPage,
  type SolveConfig,
  SolveForCalc,
} from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { solveDilution } from "../lib/dilution";

const CONC_UNITS = [
  { value: "M", label: "M" },
  { value: "mM", label: "mM" },
  { value: "µM", label: "µM" },
  { value: "nM", label: "nM" },
];
const VOL_UNITS = [
  { value: "mL", label: "mL" },
  { value: "L", label: "L" },
  { value: "µL", label: "µL" },
];

const config: SolveConfig = {
  resultTestId: "soldil-result",
  hintLeaveBlank: () => m.soldil_hint_leave_blank(),
  hintInvalid: () => m.soldil_hint_invalid(),
  resultPrefix: () => m.sci_result_solves(),
  fields: [
    {
      id: "c1",
      label: () => m.soldil_field_c1(),
      kind: "conc",
      units: CONC_UNITS,
      placeholder: "10",
    },
    {
      id: "v1",
      label: () => m.soldil_field_v1(),
      kind: "volume",
      units: VOL_UNITS,
    },
    {
      id: "c2",
      label: () => m.soldil_field_c2(),
      kind: "conc",
      units: CONC_UNITS,
      placeholder: "1",
    },
    {
      id: "v2",
      label: () => m.soldil_field_v2(),
      kind: "volume",
      units: VOL_UNITS,
      placeholder: "100",
    },
  ],
  solve: (base) => {
    const blanks = ["c1", "v1", "c2", "v2"].filter((id) => base[id] === null);
    if (blanks.length !== 1) return null;
    const value = solveDilution({
      c1: base.c1,
      v1: base.v1,
      c2: base.c2,
      v2: base.v2,
    });
    return value === null ? null : { id: blanks[0], value };
  },
};

export function CalculatorPage() {
  return (
    <SciPage>
      <SciBreadcrumb
        homeLabel={m.sci_breadcrumb_home()}
        toolName={m.soldil_tool_name()}
        toolPath="/solution-dilution"
      />

      <header className="mt-6 mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
          {m.soldil_h1()}
        </h1>
        <p className="mt-3 leading-relaxed text-muted-foreground">
          {m.soldil_intro()}
        </p>
      </header>

      <SolveForCalc config={config} />

      <SciExplainer
        howToTitle={m.sci_howto_title()}
        howTo={m.soldil_howto()}
        examplesTitle={m.sci_examples_title()}
        examples={[m.soldil_ex1(), m.soldil_ex2(), m.soldil_ex3()]}
        faqTitle={m.sci_faq_title()}
        faqs={[
          { q: m.soldil_faq_q1(), a: m.soldil_faq_a1() },
          { q: m.soldil_faq_q2(), a: m.soldil_faq_a2() },
          { q: m.soldil_faq_q3(), a: m.soldil_faq_a3() },
          { q: m.soldil_faq_q4(), a: m.soldil_faq_a4() },
        ]}
      />

      <AdPlacement className="mt-10" />
    </SciPage>
  );
}
